import { useEffect, useState } from "react";
import { MapPin, Briefcase, Globe2, ArrowUpRight, X, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { POSITIONS, MEDIA } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  full_name: "",
  email: "",
  role: "",
  location: "",
  experience_years: "",
  linkedin: "",
  cover_note: "",
};

const Careers = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");

  useEffect(() => window.scrollTo({ top: 0 }), []);

  const departments = ["All", ...Array.from(new Set(POSITIONS.map((p) => p.department)))];
  const visible = filter === "All" ? POSITIONS : POSITIONS.filter((p) => p.department === filter);

  const applyTo = (role) => {
    setForm({ ...initialForm, role });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.experience_years === "") delete payload.experience_years;
      else payload.experience_years = Number(payload.experience_years);
      await axios.post(`${API}/careers/apply`, payload);
      toast.success("Application received. Our talent team will be in touch shortly.");
      setOpen(false);
      setForm(initialForm);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="page-careers">
      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden border-b border-stone-200">
        <img src={MEDIA.office} alt="ITHR Technologies careers" className="absolute inset-0 w-full h-full object-cover opacity-60 pencil" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF7F2]/40 to-[#FAF7F2]" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 mb-8 border border-stone-300 px-3 py-1.5 backdrop-blur-md">
              <Globe2 className="w-3.5 h-3.5 text-[#B8860B]" />
              <span className="text-xs uppercase tracking-[0.25em] text-stone-700">Remote-friendly · global team</span>
            </div>
            <h1 className="font-display text-stone-900 text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter mb-8 max-w-5xl">
              Build a career,<br />
              <span className="italic text-[#B8860B]">not just a CV.</span>
            </h1>
            <p className="text-stone-700 text-lg md:text-xl max-w-2xl leading-relaxed">
              We're a small, senior team that ships hard problems for some of the world's most demanding clients. If you're curious, candid and care about craft — let's talk.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
          {[
            { title: "Remote-first", body: "Work from Dubai, Bengaluru, Lagos, Lisbon — anywhere with strong wifi and a sharp mind." },
            { title: "Senior bench", body: "No bait-and-switch. You ship with senior architects from day one." },
            { title: "Outcome-linked", body: "Pay structures that reward outcomes shipped, not hours billed." },
          ].map((b, i) => (
            <Reveal key={b.title} delay={i * 100} className="bg-white p-10" data-testid={`career-benefit-${i}`}>
              <div className="font-mono text-[#B8860B] text-sm mb-4">0{i + 1}</div>
              <h3 className="font-display text-2xl text-stone-900 mb-3">{b.title}</h3>
              <p className="text-stone-600">{b.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Open roles */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
                  Open positions
                </div>
                <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter">
                  {POSITIONS.length} roles open.<br />Where do you fit?
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {departments.map((d) => (
                  <button
                    key={d}
                    data-testid={`career-filter-${d.replaceAll(" ", "-").toLowerCase()}`}
                    onClick={() => setFilter(d)}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                      filter === d
                        ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                        : "border-stone-300 text-stone-700 hover:border-stone-500"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="border border-stone-200 divide-y divide-stone-200">
            {visible.map((p) => (
              <Reveal key={p.id} className="group" data-testid={`career-row-${p.id}`}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center p-6 md:p-8 hover:bg-stone-100 transition-colors">
                  <div className="md:col-span-5">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#B8860B] mb-2">{p.department}</div>
                    <h3 className="font-display text-xl md:text-2xl text-stone-900">{p.title}</h3>
                  </div>
                  <div className="md:col-span-3 flex items-center gap-2 text-stone-600">
                    <MapPin className="w-4 h-4" /> {p.location}
                  </div>
                  <div className="md:col-span-2 flex items-center gap-2 text-stone-600">
                    <Briefcase className="w-4 h-4" /> {p.type}
                  </div>
                  <div className="md:col-span-2 md:text-right">
                    <button
                      data-testid={`career-apply-${p.id}`}
                      onClick={() => applyTo(p.title)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 border border-stone-300 text-stone-900 hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] transition-colors"
                    >
                      Apply <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16 text-center">
            <p className="text-stone-600 mb-6">Don't see a perfect fit? We're always hiring exceptional humans.</p>
            <button
              data-testid="career-general-apply"
              onClick={() => applyTo("General interest")}
              className="btn-secondary"
            >
              Submit general interest <ArrowUpRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* Application Modal */}
      {open && (
        <div
          data-testid="career-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-stone-900/35 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white border border-stone-200 max-w-2xl w-full max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-stone-200">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] mb-1">Apply</div>
                <h3 className="font-display text-2xl text-stone-900">{form.role}</h3>
              </div>
              <button
                data-testid="career-modal-close"
                onClick={() => setOpen(false)}
                className="text-stone-600 hover:text-stone-900 p-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={submit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full name *" data-testid="career-form-name">
                  <input
                    required
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    className="form-input"
                  />
                </Field>
                <Field label="Email *" data-testid="career-form-email">
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="form-input"
                  />
                </Field>
                <Field label="Location" data-testid="career-form-location">
                  <input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="City, Country"
                    className="form-input"
                  />
                </Field>
                <Field label="Years of experience" data-testid="career-form-exp">
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={form.experience_years}
                    onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
                    className="form-input"
                  />
                </Field>
              </div>

              <Field label="LinkedIn / portfolio URL" data-testid="career-form-linkedin">
                <input
                  type="url"
                  value={form.linkedin}
                  onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                  placeholder="https://linkedin.com/in/..."
                  className="form-input"
                />
              </Field>

              <Field label="Tell us about you" data-testid="career-form-note">
                <textarea
                  rows={4}
                  value={form.cover_note}
                  onChange={(e) => setForm({ ...form, cover_note: e.target.value })}
                  placeholder="Share what you'd bring and the kind of problems you love solving."
                  className="form-input resize-none"
                />
              </Field>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-5 py-3 text-stone-700 hover:text-stone-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="career-form-submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-60"
                >
                  {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting</>) : (<>Submit application <ArrowUpRight className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

const Field = ({ label, children, ...rest }) => (
  <label className="block" {...rest}>
    <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">{label}</div>
    {children}
  </label>
);

export default Careers;
